import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// ⋯ The place's extras (PORTED_HOME_HANDOFF_2026_09_25 §0-ter, Nina 09-25 late:
// "⋯ menu + slash commands"). The room is the page, so today's chrome leaves
// it; ONE quiet ⋯ in the slim top bar keeps a visible door to every extra
// (a hidden-only feature reads as deleted): Watch together · Go live · Story ·
// Music · Share · Shelf. Each item opens the EXISTING surface (nothing is
// rebuilt here) and names its slash command when it has one. An item that is
// ON right now (a watch party, a stream) wears a dot.
//
// Popover mechanics are PortedAbout's: portaled to <body> (the bar clips),
// fixed, following the ⋯ on scroll/resize; outside press or Esc closes.
// Class names avoid every substring the generated themes target, and the
// buttons carry the theme shield (data-prop-ui) plus their own armor.
const CSS = `
.pmn-sheet, .pmn-sheet * { box-sizing: border-box; }
.pmn-sheet { position: fixed; z-index: 100020; padding: 6px; border-radius: 12px; background: #120E18; color: #EEE7F5; border: 1px solid rgba(238,231,245,0.16); box-shadow: 0 14px 34px rgba(0,0,0,0.55); text-align: left; animation: pmnIn .16s ease-out; }
.pmn-sheet .pmn-item.pmn-item { all: unset; box-sizing: border-box; display: flex; align-items: center; gap: 10px; width: 100%; min-height: 40px; padding: 8px 10px !important; border-radius: 8px !important; cursor: pointer; font: 500 14px/1.2 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif !important; color: #EEE7F5 !important; background: none !important; border: 0 !important; box-shadow: none !important; text-shadow: none !important; transform: none !important; }
.pmn-sheet .pmn-item.pmn-item:hover, .pmn-sheet .pmn-item.pmn-item:focus-visible { background: rgba(238,231,245,0.08) !important; outline: none; }
.pmn-ic { width: 22px; text-align: center; font-size: 16px; line-height: 1; flex: none; }
.pmn-lb { flex: 1; min-width: 0; }
.pmn-cmd { font: 500 12px/1 ui-monospace, "SF Mono", Menlo, monospace; color: rgba(238,231,245,0.5); }
.pmn-live { width: 8px; height: 8px; border-radius: 50%; background: #6FE7F2; box-shadow: 0 0 8px #6FE7F2; flex: none; }
/* the trigger's armor: generated themes set button padding/border/font/shadow with !important */
.pmn-dots.pmn-dots { box-sizing: border-box !important; padding: 0 !important; margin: 0 !important; border: 1px solid rgba(238,231,245,0.18) !important; border-radius: 8px !important; color: rgba(238,231,245,0.85) !important; font: 700 18px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif !important; letter-spacing: 1px !important; text-shadow: none !important; box-shadow: none !important; transform: none !important; min-width: 0 !important; min-height: 0 !important; text-transform: none !important; }
.pmn-dots:focus-visible { outline: 2px solid #F0AC55 !important; outline-offset: 2px; }
/* wave 3 (ruling 32): the slim bar's plain "‹ Back" (armored like the ⋯: generated themes style every button) */
.pmn-back.pmn-back { all: unset; box-sizing: border-box; display: inline-flex; align-items: center; gap: 4px; min-height: 32px; padding: 0 8px !important; margin: 0 !important; border: 0 !important; border-radius: 8px !important; background: transparent !important; color: #F3E8D8 !important; font: 500 15px/1 "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif !important; cursor: pointer; text-shadow: none !important; box-shadow: none !important; transform: none !important; min-width: 0 !important; text-transform: none !important; letter-spacing: 0 !important; }
.pmn-back.pmn-back:hover { background: rgba(238,231,245,0.08) !important; }
.pmn-back:focus-visible { outline: 2px solid #F0AC55 !important; outline-offset: 2px; }
@keyframes pmnIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .pmn-sheet { animation: none; } }
`;

export default function PortedMenu({ items = [], isMobile = false }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const sheetRef = useRef(null);
  const sheetId = `pmn-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  const place = useCallback(() => {
    const b = btnRef.current;
    if (!b || typeof window === 'undefined') return;
    const r = b.getBoundingClientRect();
    const vw = window.innerWidth || 0;
    const width = Math.max(200, Math.min(250, vw - 32));
    setPos({ top: r.bottom + 8, left: Math.max(16, Math.min(vw - 16 - width, r.right - width)), width });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    let raf = 0;
    const follow = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(place); };
    const onDown = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (sheetRef.current && sheetRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { setOpen(false); if (btnRef.current) btnRef.current.focus(); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const list = sheetRef.current ? [...sheetRef.current.querySelectorAll('.pmn-item')] : [];
      if (!list.length) return;
      e.preventDefault();
      const i = list.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? (i + 1) % list.length : (i <= 0 ? list.length - 1 : i - 1);
      list[next].focus();
    };
    window.addEventListener('resize', follow);
    window.addEventListener('scroll', follow, true);
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', follow);
      window.removeEventListener('scroll', follow, true);
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, place]);
  // the first item takes focus ONCE per open (keyboard users land inside; a
  // scroll that re-places the sheet never yanks focus back to it)
  const placed = !!pos;
  useEffect(() => {
    if (!open || !placed || !sheetRef.current) return;
    const first = sheetRef.current.querySelector('.pmn-item');
    if (first) first.focus({ preventScroll: true });
  }, [open, placed]);

  const live = items.some((it) => it.on);
  // (phones: 36, a hair bigger than the app's ♫ FAB beside it in the slim bar; ruling 32)
  const size = isMobile ? 36 : 34;
  return (
    <>
      <style>{CSS}</style>
      <button
        ref={btnRef}
        type="button"
        data-prop-ui=""
        className="pmn-dots"
        aria-label={`More: ${items.map((it) => String(it.label || '').toLowerCase()).join(', ')}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? sheetId : undefined}
        onClick={() => setOpen((v) => !v)}
        style={{
          '--prop-bg': open ? 'rgba(238,231,245,0.14)' : 'transparent',
          background: open ? 'rgba(238,231,245,0.14)' : 'transparent',
          position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          width: size, height: size, padding: 0, margin: 0, borderRadius: 8,
          border: '1px solid rgba(238,231,245,0.18)', color: 'rgba(238,231,245,0.85)', cursor: 'pointer',
          font: '700 18px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
          letterSpacing: 1, boxShadow: 'none', textShadow: 'none', transform: 'none',
        }}
      >
        <span aria-hidden="true" style={{ marginTop: -6 }}>⋯</span>
        {live ? <span aria-hidden="true" style={{ position: 'absolute', top: 4, right: 4, width: 7, height: 7, borderRadius: '50%', background: '#6FE7F2', boxShadow: '0 0 6px #6FE7F2' }} /> : null}
      </button>
      {open && pos && typeof document !== 'undefined' ? createPortal(
        <div ref={sheetRef} id={sheetId} className="pmn-sheet" role="menu" aria-label="More"
          style={{ top: pos.top, left: pos.left, width: pos.width }}>
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              role="menuitem"
              data-prop-ui=""
              data-menu-item={it.key}
              className="pmn-item"
              onClick={() => {
                setOpen(false);
                // focus returns to the ⋯ (a surface that takes focus moves it on)
                if (btnRef.current) btnRef.current.focus({ preventScroll: true });
                if (it.onSelect) it.onSelect();
              }}
            >
              <span className="pmn-ic" aria-hidden="true">{it.icon}</span>
              <span className="pmn-lb">{it.label}</span>
              {it.on ? <span className="pmn-live" aria-label="on now" /> : null}
              {it.cmd ? <span className="pmn-cmd">{it.cmd}</span> : null}
            </button>
          ))}
        </div>,
        document.body,
      ) : null}
    </>
  );
}
